
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, Badge } from '@/shared/ui';
import { directoriesApi, type DirectoryEntryCard } from '@/shared/api';
import type { DirectoryValue } from '@/shared/api/forms';
import styles from './DirectoryEntryDialog.module.css';

export interface DirectoryEntryDialogProps {
  open: boolean;
  value: DirectoryValue | null;
  directoryId: string | null;
  ticketId: string;
  onClose: () => void;
}

type State =
  | { kind: 'loading' }
  | { kind: 'ready'; card: DirectoryEntryCard }
  | { kind: 'gone'; reason: string };

export const DirectoryEntryDialog: React.FC<DirectoryEntryDialogProps> = ({
  open,
  value,
  directoryId,
  ticketId,
  onClose,
}) => {
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    if (!open || !value?.id || !directoryId) return;
    setState({ kind: 'loading' });
    directoriesApi
      .card(directoryId, value.id, ticketId)
      .then((res) => setState({ kind: 'ready', card: res.data }))
      .catch(() =>
        setState({
          kind: 'gone',
          reason: 'Запись удалена из справочника или закрыта для просмотра.',
        }),
      );
  }, [open, value?.id, directoryId, ticketId]);

  if (!value) return null;

  const card = state.kind === 'ready' ? state.card : null;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className={styles.dialog}>
        <DialogTitle>{value.label}</DialogTitle>

        <div className={styles.meta}>
          {card?.code && <code className={styles.code}>{card.code}</code>}
          {card?.directoryName && <span className={styles.muted}>{card.directoryName}</span>}
          {card?.archivedAt && <Badge>запись в архиве</Badge>}
          {value.custom && <Badge>свой вариант</Badge>}
        </div>

        {value.custom ? (
          <p className={styles.muted}>
            Значение вписано вручную: в справочнике такой записи нет.
          </p>
        ) : state.kind === 'loading' ? (
          <p className={styles.muted}>Загрузка…</p>
        ) : state.kind === 'gone' ? (
          <p className={styles.muted}>{state.reason}</p>
        ) : (
          <CardBody card={state.card} />
        )}
      </DialogContent>
    </Dialog>
  );
};

const CardBody: React.FC<{ card: DirectoryEntryCard }> = ({ card }) => {
  const rows = card.columns
    .map((column) => ({ column, value: card.attrs[column.key] }))
    .filter((row) => row.value != null && row.value !== '');
  const open = rows.filter((row) => !row.column.internal);
  const internal = rows.filter((row) => row.column.internal);

  return (
    <>
      {card.parent && (
        <dl className={styles.grid}>
          <div className={styles.cell}>
            <dt className={styles.label}>{card.parent.directoryName}</dt>
            <dd className={styles.value}>{card.parent.label}</dd>
          </div>
        </dl>
      )}

      {open.length > 0 && (
        <dl className={styles.grid}>
          {open.map(({ column, value }) => (
            <div key={column.key} className={styles.cell}>
              <dt className={styles.label}>{column.label}</dt>
              <dd className={styles.value}>{formatAttr(column.type, value)}</dd>
            </div>
          ))}
        </dl>
      )}

      {internal.length > 0 && (
        <>
          <p className={styles.sectionTitle}>Внутренние данные</p>
          <dl className={styles.grid}>
            {internal.map(({ column, value }) => (
              <div key={column.key} className={styles.cell}>
                <dt className={styles.label}>{column.label}</dt>
                <dd className={styles.value}>{formatAttr(column.type, value)}</dd>
              </div>
            ))}
          </dl>
        </>
      )}

      {rows.length === 0 && <p className={styles.muted}>У записи нет заполненных полей.</p>}
    </>
  );
};

function formatAttr(type: DirectoryEntryCard['columns'][number]['type'], value: unknown): string {
  if (type === 'boolean') return value === true ? 'Да' : 'Нет';
  if (type === 'date' && typeof value === 'string') {
    return new Date(`${value}T00:00:00`).toLocaleDateString('ru-RU');
  }
  return String(value);
}
